import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { AdminPasswordGate } from '../components/AdminPasswordGate';
import { useMetaTags } from '../hooks/useMetaTags';
import { programInformationIndex } from '../data/programInformationPages';
import { degreeLevelOptions, areaStudyOptions } from '../data/filterOptions';

// Internal directory of every /program-information/{degree}-{program} page.
// There are several hundred, so the list is search-first: filtered by query,
// degree level, and area of study, then capped.
const RESULT_LIMIT = 60;
export function ProgramInformationDirectoryPage() {
  useMetaTags({
    title: 'Program Information Templates | FindOnlineDegrees',
    description: 'Directory of every program-information page, one per degree level and program combination.',
    robots: 'noindex, nofollow'
  });
  const [query, setQuery] = useState('');
  const [degreeFilter, setDegreeFilter] = useState<number | 'all'>('all');
  const [areaFilter, setAreaFilter] = useState<number | 'all'>('all');
  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return programInformationIndex.filter(entry => {
      if (degreeFilter !== 'all' && entry.degreeInterest !== degreeFilter) {
        return false;
      }
      if (areaFilter !== 'all' && entry.areaId !== areaFilter) return false;
      if (!terms.length) return true;
      const haystack = `${entry.slug} ${entry.degreeLabel} ${entry.programLabel} ${entry.areaLabel} ${entry.mProgram}`.toLowerCase();
      return terms.every(term => haystack.includes(term));
    });
  }, [query, degreeFilter, areaFilter]);
  const visible = matches.slice(0, RESULT_LIMIT);
  return <AdminPasswordGate description="The program-information template directory is for the team. Enter the password to continue." data-id="element-2306" data-mp="3s5v58vzn8yet">
      <div className="bg-white" data-id="element-2307" data-mp="rg1n3zi295">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20 lg:px-8" data-id="element-2308" data-mp="3w52taq83cwpw">
          <header className="max-w-2xl" data-id="element-2309" data-mp="190arv7qk2fm0">
            <h1 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl" data-id="element-2310" data-mp="13zmve6jk56ke">
              Program information templates
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-gray-600" data-id="element-2311" data-mp="zyuldb2v2xn2">
              Every degree level and program combination has an informational
              page at{' '}
              <code className="text-base text-gray-900" data-id="element-2312" data-mp="1rph4qe4o4czw">
                /program-information/&#123;degree&#125;-&#123;program&#125;
              </code>
              , with the match form in the hero. The numeric shorthand{' '}
              <code className="text-base text-gray-900" data-id="element-2313" data-mp="1ivciy94e5rr8">
                /program-information/3-101
              </code>{' '}
              redirects to the named page.
            </p>
          </header>

          <section className="mt-12" data-id="element-2314" data-mp="2bej5zvsn7kkp">
            <p className="text-gray-600" data-id="element-2315" data-mp="26mrosbvdmr78">
              {programInformationIndex.length.toLocaleString()} combinations.
              Search by program, field, degree level, or mProgram id.
            </p>

            <div className="mt-6 space-y-4" data-id="element-2316" data-mp="2iwhfi0atqvnx">
              <div className="relative" data-id="element-2317" data-mp="2ix96exth857v">
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-2318" data-mp="2nvu16hpylwox" />
                <label htmlFor="program-search" className="sr-only" data-id="element-2319" data-mp="2wwibp2g59ex2">
                  Search program pages
                </label>
                <input id="program-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g. accounting, nursing, 500" className="w-full rounded-lg border border-gray-300 py-3 pl-12 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#1e40af]" data-id="element-2320" data-mp="31utptpdh2bl3" />
              </div>

              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by degree level" data-id="element-2321" data-mp="36ykorsvzmzcf">
                {[{
                value: 'all' as const,
                label: 'All levels'
              }, ...degreeLevelOptions].map(option => {
                const active = degreeFilter === option.value;
                return <button key={option.label} type="button" aria-pressed={active} onClick={() => setDegreeFilter(option.value as number | 'all')} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] focus-visible:ring-offset-2 ${active ? 'border-[#1e40af] bg-[#1e40af] text-white' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`} data-id="element-2322" data-mp="2o19rgdtx2fzm">
                      {option.label}
                    </button>;
              })}
              </div>

              <div data-id="element-2323" data-mp="1h8ndlge0t4qi">
                <label htmlFor="area-filter" className="mb-1.5 block text-sm font-semibold text-gray-900" data-id="element-2324" data-mp="1m53xtzi3myyb">
                  Field of study
                </label>
                <select id="area-filter" value={String(areaFilter)} onChange={e => setAreaFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="w-full max-w-sm cursor-pointer rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#1e40af]" data-id="element-2325" data-mp="2dwtd5ow356xx">
                  <option value="all" data-id="element-2326" data-mp="37e6khbj7mun3">All fields</option>
                  {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-2327" data-mp="2xi39rhaalnoj">
                      {option.label}
                    </option>)}
                </select>
              </div>
            </div>

            <p className="mt-5 text-sm text-gray-500" role="status" data-id="element-2328" data-mp="2ohx0k7lmn9v6">
              {matches.length === 0 ? 'No pages match that search.' : matches.length > visible.length ? `Showing ${visible.length} of ${matches.length.toLocaleString()} matches — keep typing to narrow it down.` : `${matches.length.toLocaleString()} ${matches.length === 1 ? 'match' : 'matches'}.`}
            </p>

            {visible.length > 0 && <ul className="mt-6 divide-y divide-gray-200 border-y border-gray-200" data-id="element-2329" data-mp="20rkkg9g8a6ti">
                {visible.map(entry => <li key={entry.slug} data-id="element-2330" data-mp="g9a2uladsotx">
                    <Link to={`/program-information/${entry.slug}`} className="group flex flex-col gap-1 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-id="element-2331" data-mp="gceaxtqyw4pv">
                      <span className="min-w-0" data-id="element-2332" data-mp="3srgn5izwfo54">
                        <span className="block font-semibold text-gray-900" data-id="element-2333" data-mp="3ssrq5glisrv6">
                          {entry.degreeLabel} in {entry.programLabel}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-gray-500" data-id="element-2334" data-mp="6jyvb3357s85">
                          /program-information/{entry.slug} · {entry.areaLabel} ·
                          degreeInterest={entry.degreeInterest} · mProgram=
                          {entry.mProgram}
                        </span>
                      </span>
                      <span className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm font-semibold text-[#1e40af]" data-id="element-2335" data-mp="3ey7e5jfv54n4">
                        View
                        <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2336" data-mp="23eeqay7e09p3" />
                      </span>
                    </Link>
                  </li>)}
              </ul>}
          </section>
        </div>
      </div>
    </AdminPasswordGate>;
}